import { ExportToCsv } from 'export-to-csv'
import * as moment from 'moment'
import { HttpResult } from 'widgets-for-react'

import { OrderedSet } from 'immutable'
import * as UrlHelpers from 'url'
import { PatternMatcher } from '../../utils/pattern_matcher'
import { prefixUrlWithLocale } from '../../utils/url'
import {
  PagedSettlements,
  Settlement,
  SettlementSpecification,
  SettlementSpecificationWithUserIds
} from './settlementsState'

type SettlementsOverrides = {
  period_start: number
  period_end: number
  created: number
  updated: number
  paid: number
}

type SettlementApiResponse = Omit<Settlement, keyof SettlementsOverrides> &
  SettlementsOverrides

type PageSettlementsApiResponse = {
  page: number
  last_page: number
  settlements: SettlementApiResponse[]
}

const settlementApiResponseParser = (x: SettlementApiResponse): Settlement => ({
  ...x,
  period_start: moment.unix(x.period_start),
  period_end: moment.unix(x.period_end),
  created: moment.unix(x.created),
  updated: moment.unix(x.updated),
  paid: x.paid ? moment.unix(x.paid) : null
})

export async function getSettlements(
  page: number
): Promise<HttpResult<PagedSettlements>> {
  const res = await fetch(
    prefixUrlWithLocale(
      `/api/opp/settlements${UrlHelpers.format({
        query: {
          page
        }
      })}`
    )
  )

  if (!res.ok) {
    return { kind: 'failed', status: res.status }
  }
  const ret = (await res.json()) as PageSettlementsApiResponse

  return {
    kind: 'result',
    value: {
      ...ret,
      settlements: ret.settlements.map(x => settlementApiResponseParser(x))
    },
    status: 200
  }
}

type SettlementSpecificationsOverrides = {
  period_start: number
  period_end: number
  created: number
  updated: number
  paid: number
}

type SettlementSpecificationApiResponse = Omit<
  SettlementSpecification,
  keyof SettlementSpecificationsOverrides
> &
  SettlementsOverrides

type SettlementSpecificationsResponse = {
  specifications: Array<{
    user_id?: number
    specification: SettlementSpecificationApiResponse
  }>
}

const settlementSpecificationApiResponseParser = (
  x: SettlementSpecificationApiResponse
): SettlementSpecification => ({
  ...x,
  period_start: moment.unix(x.period_start),
  period_end: moment.unix(x.period_end),
  created: moment.unix(x.created),
  updated: moment.unix(x.updated),
  paid: x.paid ? moment.unix(x.paid) : null
})

export async function getSettlementSpecifications(
  settlement: Settlement
): Promise<HttpResult<SettlementSpecificationWithUserIds[]>> {
  const res = await fetch(
    prefixUrlWithLocale(`/api/opp/settlements/${settlement.uid}/specifications`)
  )

  if (!res.ok) {
    return { kind: 'failed', status: res.status }
  }
  const ret = (await res.json()) as SettlementSpecificationsResponse

  return {
    kind: 'result',
    value: ret.specifications.map(x => ({
      user_id: x.user_id,
      specification: settlementSpecificationApiResponseParser(x.specification)
    })),
    status: 200
  }
}
const AttributesForCsvExport = (): OrderedSet<keyof SettlementSpecification> =>
  OrderedSet([
    'uid',
    'amount_paid',
    'amount_payable',
    'period_start',
    'period_end',
    'created',
    'updated',
    'status',
    'paid',
    'total_number_of_transactions',
    'number_of_transactions',
    'number_of_refunds',
    'number_of_withdrawals',
    'number_of_mandates',
    'number_of_internal_transfers',
    'number_of_multi_transactions',
    'total_volume',
    'transaction_volume',
    'refund_volume',
    'withdrawal_volume',
    'mandate_volume',
    'internal_transfer_volume',
    'multi_transaction_volume',
    'total_transaction_costs',
    'transaction_costs',
    'refund_costs',
    'withdrawal_costs',
    'mandate_costs',
    'internal_transfer_costs',
    'multi_transaction_costs',
    'total_order_fees',
    'total_refund_fees',
    'total_gateway_fees',
    'total_amount'
  ])

const dateToCsvValue = (x: moment.Moment): string =>
  !!x ? x.format('DD-MM-YYYY') : null

const AttributeCsvRenderer: PatternMatcher<
  keyof Settlement,
  (_: Settlement) => string | number
> = {
  uid: x => x.uid,
  amount_paid: x => x.amount_paid,
  amount_payable: x => x.amount_payable,
  period_start: x => dateToCsvValue(x.period_start),
  period_end: x => dateToCsvValue(x.period_end),
  created: x => dateToCsvValue(x.created),
  updated: x => dateToCsvValue(x.updated),
  status: x => x.status,
  paid: x => dateToCsvValue(x.paid),
  total_number_of_transactions: x => x.total_number_of_transactions,
  number_of_transactions: x => x.number_of_transactions,
  number_of_refunds: x => x.number_of_refunds,
  number_of_withdrawals: x => x.number_of_withdrawals,
  number_of_mandates: x => x.number_of_mandates,
  number_of_internal_transfers: x => x.number_of_internal_transfers,
  number_of_multi_transactions: x => x.number_of_multi_transactions,
  total_volume: x => x.total_volume,
  transaction_volume: x => x.transaction_volume,
  refund_volume: x => x.refund_volume,
  withdrawal_volume: x => x.withdrawal_volume,
  mandate_volume: x => x.mandate_volume,
  internal_transfer_volume: x => x.internal_transfer_volume,
  multi_transaction_volume: x => x.multi_transaction_volume,
  total_transaction_costs: x => x.total_transaction_costs,
  transaction_costs: x => x.transaction_costs,
  refund_costs: x => x.refund_costs,
  withdrawal_costs: x => x.withdrawal_costs,
  mandate_costs: x => x.mandate_costs,
  internal_transfer_costs: x => x.internal_transfer_costs,
  multi_transaction_costs: x => x.multi_transaction_costs,
  total_order_fees: x => x.total_order_fees,
  total_refund_fees: x => x.total_refund_fees,
  total_gateway_fees: x => x.total_gateway_fees,
  total_amount: x => x.total_amount,
  payout_type: x => x.payout_type
}

export const exportSettlementSpecificationsToCsv = (
  settlement: Settlement,
  specifications: SettlementSpecificationWithUserIds[]
) => {
  const options = {
    fieldSeparator: ',',
    filename: settlement.uid,
    useTextFile: false,
    showLabels: true,
    headers: ['user_id'].concat(AttributesForCsvExport().toArray()),
    useBom: false
  }
  const csvExporter = new ExportToCsv(options)
  csvExporter.generateCsv(
    specifications.map(x => {
      const values = AttributesForCsvExport().reduce((prev, current) => {
        prev[current] = AttributeCsvRenderer[current](x.specification) || ''
        return prev
      }, {})

      return {
        user_id: x.user_id || '',
        ...values
      }
    })
  )
}
